import React, { useState, useRef, useMemo } from 'react';
import LazyLoad from 'react-lazyload';
import { number, string } from 'prop-types';
import Slick from 'react-slick';
import DesktopFeatures from './DesktopFeatures';
import styles from './styles/styles.module.scss';
import starRating from './star_rating.png';
import { StringConstantsMixin } from '../../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import { LanguageSupportUtil } from '../../../utils/language_support_util';
import { toATLocalString } from '../../../utils/number_util';
import { DESKTOP_BREAKPOINT } from '../../../utils/constants/breakpoints';

const {
  strings: {
    HOME_PAGE: {
      EDITORS_CHOICE_BLOCK: { EDITORS_CHOICE },
      EXPLORE_AND_DISCOVER: {
        EXPLORE_WITH,
        CONFIDENCE,
        INSPIRATION,
        SEARCH_DISCOVER_NAV,
        NAVIGATE_TRACK_NAV,
        REMEMBER_INSPIRE_NAV,
        SEARCH_DISCOVER_TITLE,
        NAVIGATE_TRACK_TITLE,
        REMEMBER_INSPIRE_TITLE,
        SEARCH_DISCOVER_TEXT,
        NAVIGATE_TRACK_TEXT,
        REMEMBER_INSPIRE_TEXT
      }
    },
    SIGNUP: { SIGNUP }
  }
} = StringConstantsMixin;

const SLIDE_NAVS = [SEARCH_DISCOVER_NAV, NAVIGATE_TRACK_NAV, REMEMBER_INSPIRE_NAV];
const SLIDE_TITLES = [SEARCH_DISCOVER_TITLE, NAVIGATE_TRACK_TITLE, REMEMBER_INSPIRE_TITLE];
const SLIDE_TEXTS = [SEARCH_DISCOVER_TEXT, NAVIGATE_TRACK_TEXT, REMEMBER_INSPIRE_TEXT];
const CDN_BASE = 'https://cdn-assets.alltrails.com/assets/images/homepage/explore_and_discover/';

const ExploreAndDiscover = ({ windowWidth, languageRegionCode }) => {
  const shortRegionCode = languageRegionCode.split('-')[0];

  const isDesktop = useMemo(() => {
    return windowWidth && windowWidth >= DESKTOP_BREAKPOINT;
  }, [windowWidth]);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [opacity, setOpacity] = useState(1);
  const carouselRef = useRef();

  const navText = SLIDE_NAVS[currentSlide];
  const title = SLIDE_TITLES[currentSlide];
  const text = SLIDE_TEXTS[currentSlide];
  const carouselLength = 3;

  const slickSettings = {
    arrows: false,
    className: styles.slider,
    afterChange: index => {
      setCurrentSlide(index);
      setOpacity(1);
    },
    beforeChange: () => {
      setOpacity(0);
    }
  };

  const renderDots = () => {
    return (
      <div className={styles.dots}>
        {[...Array(carouselLength)].map((_, i) => (
          <button
            type="button"
            aria-label={`${SLIDE_NAVS[i]}`}
            className={`${styles.dot} ${currentSlide === i ? styles.active : ''}`}
            onClick={() => {
              carouselRef.current.slickGoTo(i);
            }}
          />
        ))}
      </div>
    );
  };

  const renderMobileBottomContent = () => {
    return (
      <div className={styles.bottomContainer}>
        <div className={styles.carousel}>
          <Slick ref={carouselRef} {...slickSettings}>
            <div className={styles.slideContainer}>
              <LazyLoad>
                <img className={styles.slideImage} src={`${CDN_BASE}search_discover_${shortRegionCode}.png`} />
              </LazyLoad>
            </div>
            <div className={styles.slideContainer}>
              <LazyLoad>
                <img className={styles.slideImage} src={`${CDN_BASE}navigate_${shortRegionCode}.gif`} />
              </LazyLoad>
            </div>
            <div className={styles.slideContainer}>
              <LazyLoad>
                <img className={styles.slideImage} src={`${CDN_BASE}remember_${shortRegionCode}.png`} />
              </LazyLoad>
            </div>
          </Slick>
        </div>
        {renderDots(currentSlide)}
        <div style={{ opacity }} className={styles.mobileContainer}>
          <h3 className={styles.nav}>{navText}</h3>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.featureText}>{text}</p>
          <a
            href={LanguageSupportUtil.wrapUrlSafe('/signup?ref=homepage_signup_exploreanddiscover', languageRegionCode)}
            className={styles.signUpLink}
          >
            {SIGNUP}
          </a>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.topContainer}>
        <div className={styles.content}>
          <h1 className={styles.titleTop}>{EXPLORE_WITH}</h1>
          <h1 className={styles.titleBottom}>{CONFIDENCE}</h1>
          <p className={styles.text}>{INSPIRATION}</p>
          <div className={styles.badgeStarContainer}>
            <LazyLoad>
              <img
                className={styles.badgeImage}
                src={`https://cdn-assets.alltrails.com/assets/images/editors_choice_${shortRegionCode}.png`}
                alt={EDITORS_CHOICE}
              />
            </LazyLoad>
            <span className={styles.rating}>{toATLocalString(4.9, languageRegionCode, 1)}</span>
            <LazyLoad>
              <img alt="" src={starRating} className={styles.starsImage} />
            </LazyLoad>
          </div>
        </div>
      </div>
      {isDesktop ? <DesktopFeatures languageRegionCode={languageRegionCode} /> : renderMobileBottomContent()}
    </div>
  );
};

ExploreAndDiscover.propTypes = {
  windowWidth: number,
  languageRegionCode: string
};

ExploreAndDiscover.defaultProps = {
  windowWidth: null,
  languageRegionCode: 'en-US'
};

export default ExploreAndDiscover;
